// The Marketing Hub's five places, and which pages live in each.
//
// Grant, 2026-10-29: "I do not know what I am looking at ... no real clear
// actions of what I am supposed to do ... the tabs are not obvious
// navigation." The v7 drawing had a tab bar; the build lost it and every
// page grew its own header and link row. The redesign (drawing
// docs/design/marketing-hub-redesign-mockup-v9.html, approved 2026-10-29)
// puts one switch on every Hub page: Today, Reach out, Content, Growth,
// Results, with Settings apart on the right. This file is the map behind
// that switch, kept pure so a node test can pin it.
//
// WHO SEES WHAT. A page is listed only for the people the left menu
// (sidebar-data.ts) lists it for, through the same role-visibility
// predicates; the server still refuses anything a role may not do. The
// map narrows nothing and widens nothing: it is a second door to the same
// pages, never a new one.
//
// DECISION: the pages in each place come from the approved drawing's "Where
// every page goes" table, not from the left menu's three headings
// (Families / Studio / Results), because those headings grouped pages by
// when they were built rather than by the job they do.

import { canSeeEmailSection, canSeeTeamPage, type RoleAwareUser } from "../../utils/role-visibility";

export type HubPlaceKey = "today" | "reach" | "content" | "growth" | "results" | "settings";

export interface HubPage {
  label: string;
  to: string;
  /** Extra path prefixes that belong to this page (a detail screen under a list). */
  also?: string[];
}

export interface HubPlace {
  key: HubPlaceKey;
  label: string;
  pages: HubPage[];
}

/** The five places plus Settings, with the pages this person may open. */
export function hubPlaces(user: RoleAwareUser): HubPlace[] {
  const admin = canSeeTeamPage(user);
  const today: HubPage[] = [
    { label: "Next Best Help", to: "/marketing/today" },
    { label: "Inbox", to: "/marketing/inbox" },
    ...(admin ? [{ label: "Approvals", to: "/marketing/approvals" }] : []),
    { label: "Referrals", to: "/marketing/referrals" },
  ];
  const reach: HubPage[] = [
    ...(canSeeEmailSection(user) ? [{ label: "Email my clients", to: "/marketing/email-clients" }] : []),
    { label: "Mail and cards", to: "/marketing/mail" },
    { label: "Events", to: "/marketing/events" },
    { label: "Plays", to: "/marketing/plays" },
    { label: "Share packages", to: "/marketing/studio/share" },
  ];
  const content: HubPage[] = [
    { label: "Calendar", to: "/marketing/calendar", also: ["/marketing/pieces/"] },
    { label: "Social posts", to: "/marketing/posts" },
    { label: "Content library", to: "/marketing/content" },
    { label: "Google reviews", to: "/marketing/reviews" },
    ...(admin ? [{ label: "Grant's studio", to: "/marketing/studio/founder" }] : []),
  ];
  const growth: HubPage[] = [
    { label: "Where we could help", to: "/marketing/opportunities" },
    { label: "Goals", to: "/marketing/goals/new", also: ["/marketing/goals/"] },
    { label: "Campaigns", to: "/marketing/campaigns", also: ["/marketing/series/"] },
    { label: "Growth levels", to: "/marketing/levels" },
    { label: "Playbooks", to: "/marketing/playbooks" },
  ];
  const results: HubPage[] = [
    { label: "Results", to: "/marketing/results" },
    { label: "Content results", to: "/marketing/results?tab=content", also: ["/marketing/results/content"] },
    { label: "Leaderboard", to: "/marketing/leaderboard" },
    { label: "Referral network", to: "/marketing/referral-network" },
  ];
  const places: HubPlace[] = [
    { key: "today", label: "Today", pages: today },
    { key: "reach", label: "Reach out", pages: reach },
    { key: "content", label: "Content", pages: content },
    { key: "growth", label: "Growth", pages: growth },
    { key: "results", label: "Results", pages: results },
  ];
  // Settings is Grant's and the system administrators' door in the left
  // menu; the switch shows it to the same people.
  if (admin) places.push({ key: "settings", label: "Settings", pages: [{ label: "Settings", to: "/marketing/settings" }] });
  return places;
}

function pathOf(to: string): string {
  return to.split("?")[0] ?? to;
}

function tabOf(to: string): string | null {
  const m = /[?&]tab=([^&]+)/.exec(to);
  return m?.[1] ?? null;
}

/** How well a page claims a location: 0 = not at all, higher = more exact. */
function claim(page: HubPage, pathname: string, search: string): number {
  const want = tabOf(page.to);
  const have = tabOf(search);
  if (pathname === pathOf(page.to) || pathname === `${pathOf(page.to)}/`) {
    if (want !== null) return want === have ? 4 : 0;
    return have !== null ? 2 : 3;
  }
  if (page.also?.some((p) => (p.endsWith("/") ? pathname.startsWith(p) : pathname === p))) return 2;
  if (pathname.startsWith(`${pathOf(page.to)}/`)) return 1;
  return 0;
}

/** The place and page a location belongs to, or null when it is not a Hub page. */
export function hubLocation(
  places: readonly HubPlace[],
  pathname: string,
  search = "",
): { place: HubPlace; page: HubPage; exact: boolean } | null {
  if (!pathname.startsWith("/marketing")) return null;
  if (pathname.startsWith("/marketing/settings")) {
    // Non-admins reach a few Settings pages by link (Economics from a brief,
    // Budgets, Audiences); the switch still shows where they are.
    const home: HubPage = { label: "Settings", to: "/marketing/settings" };
    const settings = places.find((p) => p.key === "settings") ?? { key: "settings" as const, label: "Settings", pages: [home] };
    return { place: settings, page: settings.pages[0] ?? home, exact: true };
  }
  let best: { place: HubPlace; page: HubPage; score: number } | null = null;
  for (const place of places) {
    for (const page of place.pages) {
      const score = claim(page, pathname, search);
      if (score > (best?.score ?? 0)) best = { place, page, score };
    }
  }
  return best ? { place: best.place, page: best.page, exact: best.score >= 3 } : null;
}
